import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import NewsletterForm from "../components/NewsletterForm";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { rolloutMode } from "../rollout";
import { toDocumentMeta, getRouteMeta } from "../route-meta";

export default function Home() {
  useDocumentMeta(toDocumentMeta(getRouteMeta("/", rolloutMode)!));
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#newsletter" || hash === "#waitlist") document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <>
      <section className="mk-container pt-10 sm:pt-16 lg:pt-24 pb-12">
        <div className="grid gap-12 lg:grid-cols-[1.15fr_1fr] lg:items-center">
          <div>
            <p className="mk-eyebrow">The PX7 Training Method</p>
            <h1 className="mk-h1 mt-4">Science-based <span className="text-px-green-dark">adaptive training.</span></h1>
            <p className="mk-lead mt-6 max-w-2xl">
              A running plan built around your goal, recent runs and available time. As you complete
              sessions and report how you feel, PX7 Training adjusts the duration, intensity and
              recovery of your upcoming runs.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#newsletter" className="mk-button-accent">Get the newsletter</a>
              <Link to="/method" className="mk-button-ghost">How the method works</Link>
            </div>
            <p className="mt-4 text-sm text-px-text-soft">Expected launch: 1 November 2026. Web and iPhone first.</p>
          </div>
          <div className="rounded-xl2 bg-px-ink text-white p-6 sm:p-8 shadow-card">
            <p className="text-xs text-px-green font-semibold">Example · your next run</p>
            <div className="flex justify-between items-baseline gap-4 mt-4">
              <h2 className="font-display text-2xl font-bold">2 × 6 min tempo</h2>
              <span className="font-mono text-sm whitespace-nowrap">35 min total</span>
            </div>
            <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-white/70">Warm-up</dt><dd>10 min easy running</dd>
              <dt className="text-white/70">Tempo</dt><dd>2 × 6 min</dd>
              <dt className="text-white/70">Recovery</dt><dd>3 min easy jog between the two tempo blocks</dd>
              <dt className="text-white/70">Cooldown</dt><dd>10 min easy running</dd>
            </dl>
            <div className="mt-5 border-y border-white/20 py-4">
              <p className="text-xs text-white/70">During the tempo blocks</p>
              <p className="mt-1 font-mono text-lg">5:45 /km <span className="text-sm text-white/80">· range 5:35–5:56</span></p>
            </div>
            <p className="mt-4 text-sm text-white/85"><strong className="text-white">Why this run?</strong> Controlled faster work for your 10K goal, within the 35 minutes you have today.</p>
            <div className="mt-5 rounded-xl bg-white/5 border border-white/20 p-4">
              <p className="font-semibold text-sm">Heavy legs? 30 min easy instead.</p>
              <p className="mt-1 text-sm text-white/75">Keep the effort conversational. Choose one session.</p>
            </div>
            <p className="mt-4 text-xs text-white/60">Example session. Your targets are based on your own running.</p>
          </div>
        </div>
      </section>

      <section className="mk-container py-14">
        <div className="max-w-3xl">
          <p className="mk-eyebrow">Planning your training</p>
          <h2 className="mk-h2 mt-3">How far, how fast and when to run.</h2>
          <p className="mt-5 text-px-text-soft">PX7 Training uses your recent runs, available time and recovery to choose the length and intensity of your next session.</p>
        </div>
        <div className="grid gap-5 md:grid-cols-3 mt-8">
          {[
            ["Your goal", "Easy runs and harder sessions", "PX7 Training adjusts the amount of harder running to your goal, experience and recovery. The balance between easy and hard sessions can change as your training progresses."],
            ["Your session", "Pace, duration and recoveries", "See the purpose, duration, pace or power, and recovery between efforts. For example: tempo at 5:45/km, with a range of 5:35–5:56/km. On easy days, keep the effort comfortable."],
            ["Your week", "Room for changes", "Run a day later, shorten a session or miss it altogether. PX7 Training uses what you completed to plan the rest of your week."],
          ].map(([label, title, body]) => <article className="mk-card" key={label}>
            <p className="text-xs font-semibold text-px-green-dark">{label}</p>
            <h3 className="mk-h3 mt-3">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-px-text-soft">{body}</p>
          </article>)}
        </div>
      </section>

      <section className="mk-container py-14">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <p className="mk-eyebrow">Your next sessions</p>
            <h2 className="mk-h2 mt-3">Focus on the next few runs.</h2>
            <p className="mt-5 text-px-text-soft">Keep the upcoming runs in view, with their duration and purpose. Your goal and race date guide the longer-term planning.</p>
            <p className="mt-4 text-px-text-soft">For the next run, PX7 Training gives you a recommendation and, when useful, one alternative. A shorter session may fit the time you have. An easy run may suit your legs better today.</p>
            <Link to="/method" className="inline-block mt-6 text-sm font-semibold text-px-green-dark hover:underline">See how a session is chosen →</Link>
          </div>
          <ol className="divide-y divide-px-line border-y border-px-line">
            {[
              ["Before", "Your time and how you feel", "Tell PX7 Training how much time you have and whether you feel ready for the session. Warm-up, recoveries and cooldown count too."],
              ["During", "Clear instructions", "Follow the pace or power for each part. Keep easy runs at an effort where you can talk comfortably, including on hills or in heat."],
              ["After", "What you actually ran", "Log the run and how it felt. If you shortened an interval, record the minutes you completed so the plan can adjust."],
            ].map(([label, title, body]) => <li key={label} className="py-6">
              <p className="text-xs font-semibold text-px-green-dark">{label}</p>
              <h3 className="mk-h3 mt-1">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-px-text-soft">{body}</p>
            </li>)}
          </ol>
        </div>
      </section>

      <section className="mk-container py-14">
        <div className="mk-card grid gap-8 lg:grid-cols-2">
          <div>
            <p className="mk-eyebrow">The research</p>
            <h2 className="mk-h2 mt-3">Research guides the plan. Your training helps adjust it.</h2>
          </div>
          <div>
            <p className="text-px-text-soft">Research informs the choice of sessions, the amount of harder running and the recovery between runs. Your completed training and feedback help us apply that knowledge to you.</p>
            <p className="mt-4 text-px-text-soft">We keep improving the method as research develops and we evaluate how the training works in practice.</p>
            <Link to="/science" className="inline-block mt-5 text-sm font-semibold text-px-green-dark hover:underline">See the research behind the choices →</Link>
          </div>
        </div>
      </section>

      <section className="mk-container py-14">
        <div className="max-w-3xl">
          <p className="mk-eyebrow">On your phone</p>
          <h2 className="mk-h2 mt-3">Your plan on the web and iPhone.</h2>
          <p className="mt-5 text-px-text-soft">Check the session, choose your run and record it afterwards. Web and iPhone come first, with Android to follow. You can log runs manually or add watch data such as pace and heart rate.</p>
          <p className="mt-4 text-px-text-soft">The app is being built to use data from Apple Watch, Garmin and Stryd alongside your own feedback.</p>
        </div>
      </section>

      <section id="newsletter" className="mk-container pt-6 scroll-mt-24">
        <div className="grid gap-8 border-t border-px-line pt-10 lg:grid-cols-2 lg:gap-10">
          <div>
            <p className="mk-eyebrow">Expected launch · 1 November 2026</p>
            <h2 className="mk-h2 mt-3">Follow PX7 Training.</h2>
            <p className="mt-4 text-px-text-soft">We expect to launch on 1 November 2026. Get news about the app, the training method and the launch by email.</p>
          </div>
          <NewsletterForm />
        </div>
      </section>
    </>
  );
}
